import React from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, LogOut, Crown, Package, Globe2 } from "lucide-react";
import { useApp } from "@/AppContext";

export default function Navbar({ onCart, onMembership, onOrders, onSell }) {
  const { t, lang, toggleLang, user, logout, cart } = useApp();
  const navigate = useNavigate();

  const proDaysLeft = (() => {
    if (user?.membership !== "pro" || !user?.membership_expires_at) return null;
    const diff = Math.ceil((new Date(user.membership_expires_at) - new Date()) / 86400000);
    return diff > 0 ? diff : 0;
  })();

  return (
    <header className="sticky top-0 z-40 border-b border-cyan-500/20 bg-white/80 backdrop-blur-xl" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src="/logo.png" alt="PartSpare" className="w-9 h-9 object-contain" />
          <span className="font-display font-black tracking-wider ps-neon-text">PARTSPARE</span>
        </button>

        <div className="flex items-center gap-2 sm:gap-3">
          {user?.membership !== "pro" && (
            <span className="hidden md:flex items-center gap-1.5 text-xs font-mono-ps text-slate-600" data-testid="membership-badge">
              {t("searches_left")}: {Math.max(0, (user?.search_limit || 10) - (user?.search_count || 0))}/{user?.search_limit || 10}
            </span>
          )}

          {proDaysLeft !== null && (
            <span data-testid="pro-badge" className="hidden sm:flex items-center gap-1.5 text-xs font-head font-bold bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 px-2.5 py-1 rounded-full">
              <Crown size={13} className="text-amber-500" /> Pro • {proDaysLeft} {t("days_short")}
            </span>
          )}

          {user?.membership === "pro" && (
            <button data-testid="nav-sell-btn" onClick={onSell} title={t("sell_parts")}
              className="p-2 rounded-lg hover:bg-cyan-500/10 text-slate-600 hover:text-cyan-700 transition">
              <Package size={20} />
            </button>
          )}

          <button data-testid="nav-membership-btn" onClick={onMembership} title={t("membership")}
            className="flex items-center gap-1.5 text-xs font-head font-bold border border-amber-400 text-amber-600 px-3 py-1.5 rounded-full hover:bg-amber-50 transition">
            <Crown size={14} /> <span className="hidden sm:inline">{t("membership")}</span>
          </button>

          <button data-testid="nav-orders-btn" onClick={onOrders} title={t("orders")}
            className="p-2 rounded-lg hover:bg-cyan-500/10 text-slate-600 hover:text-cyan-700 transition hidden sm:block">
            <Globe2 size={20} />
          </button>

          <button data-testid="nav-cart-btn" onClick={onCart} title={t("cart")}
            className="relative p-2 rounded-lg hover:bg-cyan-500/10 text-slate-600 hover:text-cyan-700 transition">
            <ShoppingCart size={20} />
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-cyan-400 text-slate-950 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {cart.length}
              </span>
            )}
          </button>

          <button data-testid="nav-lang-btn" onClick={toggleLang}
            className="font-mono-ps text-xs border border-cyan-500/40 rounded-full px-3 py-1 text-cyan-700 hover:bg-cyan-500/10 transition">
            {lang === "TR" ? "EN" : "TR"}
          </button>

          <button data-testid="nav-logout-btn" onClick={logout} title={t("logout")}
            className="p-2 rounded-lg hover:bg-red-500/10 text-slate-600 hover:text-red-400 transition">
            <LogOut size={20} />
          </button>
        </div>
      </div>
    </header>
  );
}
